import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';

export interface CampaignReleaseWizardCampaign {
  id: string;
  key: string;
  publishedAt: string | null;
  config: Record<string, unknown>;
  version?: number;
}

interface CampaignReleaseWizardProps {
  open: boolean;
  onClose: () => void;
  campaigns: CampaignReleaseWizardCampaign[];
  onPublished: () => void;
}

/**
 * Guided validate → preview → publish for draft referral campaigns.
 */
export const CampaignReleaseWizard: React.FC<CampaignReleaseWizardProps> = ({
  open,
  onClose,
  campaigns,
  onPublished,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [campaignId, setCampaignId] = useState('');
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setCampaignId('');
      setWizardError(null);
      setPreviewText('');
      setPreviewLoading(false);
    }
  }, [open]);

  const draftCampaigns = campaigns.filter((c) => !c.publishedAt);
  const selected = campaigns.find((c) => c.id === campaignId);

  useEffect(() => {
    if (!open || step !== 2 || !selected) return;
    let cancelled = false;
    setPreviewLoading(true);
    setWizardError(null);
    (async () => {
      try {
        const result = await apiService.previewReferralCampaign(selected.config, {
          country: 'GB',
          stakeCents: 1000,
          oddsInt: 18000,
        });
        if (!cancelled) {
          setPreviewText(
            `Sample (GB, £10 stake, 1.80 odds): referrer £${(result.referrerCents / 100).toFixed(2)}, referee £${(
              result.refereeCents / 100
            ).toFixed(2)}`
          );
        }
      } catch (e) {
        if (!cancelled) {
          setWizardError(e instanceof Error ? e.message : 'Preview failed');
          setPreviewText('');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, step, selected]);

  const validateStep = async () => {
    setWizardError(null);
    if (!selected) {
      setWizardError('Select a draft campaign.');
      return;
    }
    try {
      const result = await apiService.validateReferralCampaign(selected.config);
      if (!result.valid) {
        setWizardError(result.errors.join('; '));
        return;
      }
      setStep(2);
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : 'Validate failed');
    }
  };

  const publishStep = async () => {
    setWizardError(null);
    if (!selected) return;
    try {
      await apiService.publishReferralCampaign(selected.id);
      onPublished();
      onClose();
    } catch (e) {
      setWizardError(e instanceof Error ? e.message : 'Publish failed');
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Guided campaign release</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0, color: 'var(--text-muted)', fontSize: 14 }}>
            Unpublished campaigns only: validate config, review a sample payout preview, then publish.
          </p>
          {draftCampaigns.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No draft campaigns. Use &quot;New Campaign&quot; first.</p>
          ) : (
            <>
              <div className="form-group">
                <label>Draft campaign</label>
                <select
                  value={campaignId}
                  onChange={(e) => {
                    setCampaignId(e.target.value);
                    setStep(1);
                    setPreviewText('');
                    setWizardError(null);
                  }}
                  disabled={step === 2}
                  style={{ width: '100%', padding: 10 }}
                >
                  <option value="">— Select —</option>
                  {draftCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.key} (v{c.version})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 12, fontSize: 13 }}>
                <strong>Step {step}</strong> of 2 {step === 1 ? '— Validate' : '— Preview & publish'}
              </div>
              {wizardError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{wizardError}</div>}
              {step === 2 && (
                <div style={{ padding: 12, background: 'var(--input)', borderRadius: 8, marginBottom: 12, minHeight: 48 }}>
                  {previewLoading ? 'Loading preview…' : previewText || '—'}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            Cancel
          </button>
          {draftCampaigns.length > 0 && step === 1 && (
            <button type="button" className="btn btn-primary" onClick={validateStep} disabled={!selected}>
              Validate &amp; continue
            </button>
          )}
          {draftCampaigns.length > 0 && step === 2 && (
            <>
              <button
                type="button"
                className="button-secondary"
                onClick={() => {
                  setStep(1);
                  setPreviewText('');
                }}
              >
                Back
              </button>
              <button type="button" className="btn btn-success" onClick={publishStep} disabled={previewLoading || !previewText}>
                Publish campaign
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
